import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile, uploadMultipleFiles } from '../../services/upload';
import './CommerceAdmin.css';

const initialShopForm = {
  name: '',
  ownerName: '',
  phone: '',
  email: '',
  password: '',
  address: '',
  district: '',
  state: '',
  latitude: '',
  longitude: '',
  logoUrl: '',
  coverImageUrl: ''
};

const initialProductForm = {
  name: '',
  shopId: '',
  categoryId: '',
  keywords: '',
  tags: '',
  image: '',
  images: [],
  unit: '',
  price: '',
  status: 'active',
  isGlobal: true
};

const initialAdForm = {
  shopId: '',
  productId: '',
  categoryId: '',
  title: '',
  description: '',
  posterImage: '',
  productImage: '',
  originalPrice: '',
  offerPrice: '',
  durationDays: 10,
  scheduledAt: '',
  validityDate: '',
  isProductOffer: false,
  isFeatured: false,
  isPinned: false
};

const tabs = [
  ['overview', 'Overview'],
  ['shops', 'Shops'],
  ['products', 'Products'],
  ['ads', 'Ads & Offers'],
  ['users', 'Users'],
  ['subscriptions', 'Subscriptions'],
  ['nearby', 'Nearby'],
  ['districts', 'Districts'],
  ['media', 'Media'],
  ['notifications', 'Notifications'],
  ['roles', 'Roles']
];

const splitLines = (value) => String(value || '')
  .split(/[\n,]/)
  .map((item) => item.trim())
  .filter(Boolean);

const money = (value, currency = 'INR') => {
  const amount = Number(value || 0);
  return `${currency} ${amount.toLocaleString('en-IN')}`;
};

const StatusBadge = ({ value }) => (
  <span className={`commerce-badge commerce-badge-${String(value || 'none').toLowerCase()}`}>
    {value || 'none'}
  </span>
);

const CommerceAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [theme, setTheme] = useState(localStorage.getItem('commerceTheme') || 'dark');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [ads, setAds] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [media, setMedia] = useState([]);
  const [roles, setRoles] = useState([]);
  const [districtStats, setDistrictStats] = useState([]);
  const [nearbyOffers, setNearbyOffers] = useState([]);
  const [nearbyShops, setNearbyShops] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [shopForm, setShopForm] = useState(initialShopForm);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [adForm, setAdForm] = useState(initialAdForm);
  const [subscriptionForm, setSubscriptionForm] = useState({ shopId: '', planId: '', amountPaid: '', paymentStatus: 'manual', status: 'manual' });
  const [nearbyForm, setNearbyForm] = useState({ lat: '', lng: '', radius: 5000, search: '' });
  const [notificationForm, setNotificationForm] = useState({ target: 'all', district: '', title: '', message: '', type: 'system' });
  const [filters, setFilters] = useState({ shopSearch: '', district: '', adStatus: '', userSearch: '', productSearch: '', q: '' });
  const [pagination, setPagination] = useState({
    shops: { page: 1, pages: 1 },
    products: { page: 1, pages: 1 },
    ads: { page: 1, pages: 1 },
    users: { page: 1, pages: 1 },
    subscriptions: { page: 1, pages: 1 }
  });

  const notify = (type, text) => {
    setToast({ type, text });
    window.clearTimeout(window.__commerceToastTimer);
    window.__commerceToastTimer = window.setTimeout(() => setToast(null), 3500);
  };

  const setPage = (key, page) => {
    setPagination((current) => ({
      ...current,
      [key]: { ...current[key], page }
    }));
  };

  const updateTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('commerceTheme', next);
  };

  const fetchBaseData = async () => {
    const [shopsRes, categoriesRes, plansRes] = await Promise.all([
      api.get('/api/super/shops?limit=100&includeMeta=true'),
      api.get('/api/super/categories'),
      api.get('/api/super/plans')
    ]);
    setShops(shopsRes.data.data || []);
    setCategories(categoriesRes.data.data || []);
    setPlans(plansRes.data.data || []);
  };

  const loadOverview = async () => {
    const response = await api.get('/api/super/analytics');
    setAnalytics(response.data.data);
  };

  const loadShops = async () => {
    const params = {
      page: pagination.shops.page,
      limit: 10,
      includeMeta: true
    };
    if (filters.shopSearch) params.name = filters.shopSearch;
    if (filters.district) params.district = filters.district;
    const response = await api.get('/api/super/shops', { params });
    setShops(response.data.data || []);
    setPagination((current) => ({ ...current, shops: response.data.pagination || current.shops }));
  };

  const loadProducts = async () => {
    const params = {
      page: pagination.products.page,
      limit: 10,
      scope: 'all'
    };
    if (filters.productSearch) params.search = filters.productSearch;
    if (filters.district) params.district = filters.district;
    const response = await api.get('/api/super/products', { params });
    setProducts(response.data.data || []);
    setPagination((current) => ({ ...current, products: response.data.pagination || current.products }));
  };

  const loadAds = async () => {
    const params = {
      page: pagination.ads.page,
      limit: 10
    };
    if (filters.district) params.district = filters.district;
    if (filters.adStatus) params.status = filters.adStatus;
    const response = await api.get('/api/super/ads', { params });
    setAds(response.data.data || []);
    setPagination((current) => ({ ...current, ads: response.data.pagination || current.ads }));
  };

  const loadUsers = async () => {
    const params = {
      page: pagination.users.page,
      limit: 10
    };
    if (filters.userSearch) params.search = filters.userSearch;
    if (filters.district) params.district = filters.district;
    const response = await api.get('/api/super/users', { params });
    setUsers(response.data.data || []);
    setPagination((current) => ({ ...current, users: response.data.pagination || current.users }));
  };

  const loadSubscriptions = async () => {
    const response = await api.get('/api/super/subscriptions', {
      params: {
        page: pagination.subscriptions.page,
        limit: 10
      }
    });
    setSubscriptions(response.data.data || []);
    setPagination((current) => ({ ...current, subscriptions: response.data.pagination || current.subscriptions }));
  };

  const loadMedia = async () => {
    const response = await api.get('/api/super/media');
    setMedia(response.data.data || []);
  };

  const loadDistricts = async () => {
    const response = await api.get('/api/super/districts/stats');
    setDistrictStats(response.data.data || []);
  };

  const loadRoles = async () => {
    const response = await api.get('/api/super/roles');
    setRoles(response.data.data || []);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await fetchBaseData();
        await loadOverview();
      } catch (error) {
        notify('error', error.response?.data?.message || 'Unable to load admin data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadTab = async () => {
      setLoading(true);
      try {
        if (activeTab === 'overview') await loadOverview();
        if (activeTab === 'shops') await loadShops();
        if (activeTab === 'products') await loadProducts();
        if (activeTab === 'ads') await loadAds();
        if (activeTab === 'users') await loadUsers();
        if (activeTab === 'subscriptions') await loadSubscriptions();
        if (activeTab === 'media') await loadMedia();
        if (activeTab === 'districts') await loadDistricts();
        if (activeTab === 'roles') await loadRoles();
      } catch (error) {
        notify('error', error.response?.data?.message || 'Unable to refresh this section');
      } finally {
        setLoading(false);
      }
    };
    loadTab();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.shops.page, pagination.products.page, pagination.ads.page, pagination.users.page, pagination.subscriptions.page]);

  const shopOptions = useMemo(() => shops.map((shop) => (
    <option key={shop._id} value={shop._id}>{shop.name || shop.shopName}</option>
  )), [shops]);

  const categoryOptions = useMemo(() => categories.map((category) => (
    <option key={category._id} value={category._id}>{category.name}</option>
  )), [categories]);

  const productOptions = useMemo(() => products.map((product) => (
    <option key={product._id} value={product._id}>{product.name}</option>
  )), [products]);

  const submitShop = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/super/shops', shopForm);
      setShopForm(initialShopForm);
      notify('success', 'Shop created');
      await loadShops();
      await fetchBaseData();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Shop save failed');
    }
  };

  const setShopStatus = async (shop, status) => {
    try {
      await api.patch(`/api/super/shops/${shop._id}/status`, { status });
      notify('success', `Shop ${status}`);
      await loadShops();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Status update failed');
    }
  };

  const resetShopPassword = async (shop) => {
    const password = window.prompt('New shop password');
    if (!password) return;
    try {
      await api.post(`/api/super/shops/${shop._id}/reset-password`, { password });
      notify('success', 'Password reset');
    } catch (error) {
      notify('error', error.response?.data?.message || 'Password reset failed');
    }
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/super/products', {
        ...productForm,
        keywords: splitLines(productForm.keywords),
        tags: splitLines(productForm.tags),
        price: productForm.price === '' ? undefined : Number(productForm.price),
        isGlobal: !productForm.shopId
      });
      setProductForm(initialProductForm);
      notify('success', 'Product created');
      await loadProducts();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Product save failed');
    }
  };

  const uploadProductImages = async (files) => {
    if (!files?.length) return;
    const result = files.length > 1 ? await uploadMultipleFiles(files) : await uploadFile(files[0]);
    if (!result.success) {
      notify('error', result.message || 'Upload failed');
      return;
    }
    const urls = result.urls || [result.url];
    setProductForm((current) => ({
      ...current,
      image: current.image || urls[0],
      images: [...(current.images || []), ...urls]
    }));
  };

  const submitAd = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/super/ads', {
        ...adForm,
        originalPrice: adForm.originalPrice === '' ? undefined : Number(adForm.originalPrice),
        offerPrice: adForm.offerPrice === '' ? undefined : Number(adForm.offerPrice),
        durationDays: Number(adForm.durationDays || 10)
      });
      setAdForm(initialAdForm);
      notify('success', 'Ad created');
      await loadAds();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Ad save failed');
    }
  };

  const uploadAdImage = async (file, field) => {
    if (!file) return;
    const result = await uploadFile(file);
    if (!result.success) {
      notify('error', result.message || 'Upload failed');
      return;
    }
    setAdForm((current) => ({ ...current, [field]: result.url }));
  };

  const moderateAd = async (ad, patch) => {
    try {
      await api.patch(`/api/super/ads/${ad._id}/moderation`, patch);
      notify('success', 'Ad updated');
      await loadAds();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Ad update failed');
    }
  };

  const submitSubscription = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/api/super/shops/${subscriptionForm.shopId}/subscription`, {
        planId: subscriptionForm.planId,
        amountPaid: subscriptionForm.amountPaid === '' ? undefined : Number(subscriptionForm.amountPaid),
        paymentStatus: subscriptionForm.paymentStatus,
        status: subscriptionForm.status
      });
      setSubscriptionForm({ shopId: '', planId: '', amountPaid: '', paymentStatus: 'manual', status: 'manual' });
      notify('success', 'Subscription assigned');
      await loadSubscriptions();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Subscription update failed');
    }
  };

  const updateUser = async (user, patch) => {
    try {
      await api.patch(`/api/super/users/${user._id}/status`, patch);
      notify('success', 'User updated');
      await loadUsers();
    } catch (error) {
      notify('error', error.response?.data?.message || 'User update failed');
    }
  };

  const testNearby = async () => {
    try {
      const [offersRes, shopsRes] = await Promise.all([
        api.get('/api/super/nearby/offers', { params: nearbyForm }),
        api.get('/api/super/nearby/shops', { params: nearbyForm })
      ]);
      setNearbyOffers(offersRes.data.data || []);
      setNearbyShops(shopsRes.data.data || []);
      notify('success', 'Nearby results loaded');
    } catch (error) {
      notify('error', error.response?.data?.message || 'Nearby test failed');
    }
  };

  const runSearch = async () => {
    if (!filters.q) return;
    try {
      const response = await api.get('/api/super/search/suggestions', { params: { q: filters.q } });
      setSearchResult(response.data.data);
    } catch (error) {
      notify('error', error.response?.data?.message || 'Search failed');
    }
  };

  const uploadMedia = async (file, type) => {
    if (!file) return;
    const result = await uploadFile(file, type === 'logo' ? 'logo' : 'single');
    if (!result.success) {
      notify('error', result.message || 'Upload failed');
      return;
    }
    notify('success', 'Media uploaded');
    await loadMedia();
  };

  const deleteMedia = async (item) => {
    try {
      await api.delete('/api/super/media', { data: { filename: item.filename, folder: item.folder } });
      notify('success', 'Media deleted');
      await loadMedia();
    } catch (error) {
      notify('error', error.response?.data?.message || 'Media delete failed');
    }
  };

  const sendNotification = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/super/notifications', notificationForm);
      setNotificationForm({ target: 'all', district: '', title: '', message: '', type: 'system' });
      notify('success', 'Notification sent');
    } catch (error) {
      notify('error', error.response?.data?.message || 'Notification failed');
    }
  };

  return (
    <div className={`commerce-admin commerce-${theme}`}>
      <Navbar />
      {toast && <div className={`commerce-toast commerce-toast-${toast.type}`}>{toast.text}</div>}
      <div className="commerce-shell">
        <aside className="commerce-sidebar">
          <div className="commerce-brand-block">
            <span>Offera</span>
            <strong>Super Admin</strong>
          </div>
          <nav className="commerce-tabs">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={activeTab === key ? 'active' : ''}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </nav>
          <button type="button" className="commerce-theme-toggle" onClick={updateTheme}>
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
        </aside>

        <main className="commerce-main">
          <header className="commerce-header">
            <div>
              <h1>Commerce Control Center</h1>
              <p>{tabs.find(([key]) => key === activeTab)?.[1]}</p>
            </div>
            <div className="commerce-search">
              <input
                value={filters.q}
                onChange={(event) => setFilters({ ...filters, q: event.target.value })}
                placeholder="Search onion, offers, shops"
              />
              <button type="button" onClick={runSearch}>Search</button>
            </div>
          </header>

          {loading && <div className="commerce-loading">Loading...</div>}

          {searchResult && (
            <section className="commerce-panel">
              <div className="commerce-panel-header">
                <h2>Search Suggestions</h2>
                <button type="button" onClick={() => setSearchResult(null)}>Close</button>
              </div>
              <div className="commerce-mini-grid">
                {(searchResult.products || []).map((item) => <span key={item._id}>{item.name}</span>)}
                {(searchResult.offers || []).map((item) => <span key={item._id}>{item.title}</span>)}
                {(searchResult.shops || []).map((item) => <span key={item._id}>{item.name}</span>)}
              </div>
            </section>
          )}

          {activeTab === 'overview' && analytics && (
            <>
              <section className="commerce-stat-grid">
                {Object.entries(analytics.totals || {}).map(([key, value]) => (
                  <div className="commerce-stat" key={key}>
                    <span>{key.replace(/([A-Z])/g, ' $1')}</span>
                    <strong>{Number(value || 0).toLocaleString('en-IN')}</strong>
                  </div>
                ))}
              </section>
              <div className="commerce-two-column">
                <section className="commerce-panel">
                  <h2>Most Viewed Offers</h2>
                  <div className="commerce-list">
                    {(analytics.mostViewedOffers || []).map((ad) => (
                      <div key={ad._id} className="commerce-list-row">
                        <span>{ad.title}</span>
                        <strong>{ad.viewCount || 0} views</strong>
                      </div>
                    ))}
                  </div>
                </section>
                <section className="commerce-panel">
                  <h2>Top Districts</h2>
                  <div className="commerce-list">
                    {(analytics.topDistricts || []).map((district) => (
                      <div key={district.district} className="commerce-list-row">
                        <span>{district.district}</span>
                        <strong>{district.shops} shops</strong>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </>
          )}

          {activeTab === 'shops' && (
            <section className="commerce-grid-with-form">
              <form className="commerce-panel commerce-form" onSubmit={submitShop}>
                <h2>Add Shop</h2>
                <div className="commerce-form-grid">
                  <input placeholder="Shop name" value={shopForm.name} onChange={(event) => setShopForm({ ...shopForm, name: event.target.value })} required />
                  <input placeholder="Owner name" value={shopForm.ownerName} onChange={(event) => setShopForm({ ...shopForm, ownerName: event.target.value })} />
                  <input placeholder="Phone" value={shopForm.phone} onChange={(event) => setShopForm({ ...shopForm, phone: event.target.value })} />
                  <input placeholder="Email" type="email" value={shopForm.email} onChange={(event) => setShopForm({ ...shopForm, email: event.target.value })} required />
                  <input placeholder="Password" type="password" value={shopForm.password} onChange={(event) => setShopForm({ ...shopForm, password: event.target.value })} required />
                  <input placeholder="District" value={shopForm.district} onChange={(event) => setShopForm({ ...shopForm, district: event.target.value })} required />
                  <input placeholder="State" value={shopForm.state} onChange={(event) => setShopForm({ ...shopForm, state: event.target.value })} required />
                  <input placeholder="Latitude" value={shopForm.latitude} onChange={(event) => setShopForm({ ...shopForm, latitude: event.target.value })} required />
                  <input placeholder="Longitude" value={shopForm.longitude} onChange={(event) => setShopForm({ ...shopForm, longitude: event.target.value })} required />
                  <input placeholder="Logo URL" value={shopForm.logoUrl} onChange={(event) => setShopForm({ ...shopForm, logoUrl: event.target.value })} />
                </div>
                <textarea placeholder="Address" value={shopForm.address} onChange={(event) => setShopForm({ ...shopForm, address: event.target.value })} />
                <button type="submit">Create Shop</button>
              </form>

              <section className="commerce-panel">
                <div className="commerce-panel-header">
                  <h2>Shops</h2>
                  <div className="commerce-filter-row">
                    <input placeholder="Name" value={filters.shopSearch} onChange={(event) => setFilters({ ...filters, shopSearch: event.target.value })} />
                    <input placeholder="District" value={filters.district} onChange={(event) => setFilters({ ...filters, district: event.target.value })} />
                    <button type="button" onClick={loadShops}>Filter</button>
                  </div>
                </div>
                <div className="commerce-table-wrap">
                  <table>
                    <thead>
                      <tr><th>Shop</th><th>Owner</th><th>District</th><th>Subscription</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {shops.map((shop) => (
                        <tr key={shop._id}>
                          <td>{shop.name}</td>
                          <td>{shop.ownerName || shop.admin?.email || 'N/A'}</td>
                          <td>{shop.district}</td>
                          <td>{shop.subscription?.planId?.name || shop.subscriptionStatus}</td>
                          <td><StatusBadge value={shop.status || (shop.isActive ? 'active' : 'inactive')} /></td>
                          <td>
                            <div className="commerce-actions">
                              <button type="button" onClick={() => setShopStatus(shop, 'active')}>Activate</button>
                              <button type="button" onClick={() => setShopStatus(shop, 'suspended')}>Suspend</button>
                              <button type="button" onClick={() => resetShopPassword(shop)}>Reset</button>
                              <button type="button" className="danger" onClick={() => setShopStatus(shop, 'deleted')}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination pageInfo={pagination.shops} onPage={(page) => setPage('shops', page)} />
              </section>
            </section>
          )}

          {activeTab === 'products' && (
            <section className="commerce-grid-with-form">
              <form className="commerce-panel commerce-form" onSubmit={submitProduct}>
                <h2>Add Product</h2>
                <div className="commerce-form-grid">
                  <input placeholder="Product name" value={productForm.name} onChange={(event) => setProductForm({ ...productForm, name: event.target.value })} required />
                  <select value={productForm.shopId} onChange={(event) => setProductForm({ ...productForm, shopId: event.target.value })}>
                    <option value="">Global catalog</option>
                    {shopOptions}
                  </select>
                  <select value={productForm.categoryId} onChange={(event) => setProductForm({ ...productForm, categoryId: event.target.value })}>
                    <option value="">Category</option>
                    {categoryOptions}
                  </select>
                  <input placeholder="Unit" value={productForm.unit} onChange={(event) => setProductForm({ ...productForm, unit: event.target.value })} />
                  <input placeholder="Price" type="number" value={productForm.price} onChange={(event) => setProductForm({ ...productForm, price: event.target.value })} />
                  <select value={productForm.status} onChange={(event) => setProductForm({ ...productForm, status: event.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <textarea placeholder="Keywords, comma or line separated" value={productForm.keywords} onChange={(event) => setProductForm({ ...productForm, keywords: event.target.value })} />
                <textarea placeholder="Tags, comma or line separated" value={productForm.tags} onChange={(event) => setProductForm({ ...productForm, tags: event.target.value })} />
                <input type="file" accept="image/*" multiple onChange={(event) => uploadProductImages(event.target.files)} />
                <div className="commerce-preview-row">
                  {(productForm.images || []).map((url) => <img key={url} src={url} alt="" />)}
                </div>
                <button type="submit">Create Product</button>
              </form>
              <DataPanel
                title="Products"
                filters={(
                  <div className="commerce-filter-row">
                    <input placeholder="Search" value={filters.productSearch} onChange={(event) => setFilters({ ...filters, productSearch: event.target.value })} />
                    <button type="button" onClick={loadProducts}>Filter</button>
                  </div>
                )}
              >
                <div className="commerce-table-wrap">
                  <table>
                    <thead><tr><th>Product</th><th>Shop</th><th>Category</th><th>Searches</th><th>Status</th></tr></thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product._id}>
                          <td>{product.name}</td>
                          <td>{product.shopId?.name || 'Global'}</td>
                          <td>{product.categoryId?.name || 'N/A'}</td>
                          <td>{product.searchCount || 0}</td>
                          <td><StatusBadge value={product.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination pageInfo={pagination.products} onPage={(page) => setPage('products', page)} />
              </DataPanel>
            </section>
          )}

          {activeTab === 'ads' && (
            <section className="commerce-grid-with-form">
              <form className="commerce-panel commerce-form" onSubmit={submitAd}>
                <h2>Create Ad or Offer</h2>
                <div className="commerce-form-grid">
                  <select value={adForm.shopId} onChange={(event) => setAdForm({ ...adForm, shopId: event.target.value })} required>
                    <option value="">Shop</option>
                    {shopOptions}
                  </select>
                  <select value={adForm.categoryId} onChange={(event) => setAdForm({ ...adForm, categoryId: event.target.value })}>
                    <option value="">Category</option>
                    {categoryOptions}
                  </select>
                  <select value={adForm.productId} onChange={(event) => setAdForm({ ...adForm, productId: event.target.value, isProductOffer: Boolean(event.target.value) })}>
                    <option value="">Product</option>
                    {productOptions}
                  </select>
                  <input placeholder="Title" value={adForm.title} onChange={(event) => setAdForm({ ...adForm, title: event.target.value })} required />
                  <input placeholder="Original price" type="number" value={adForm.originalPrice} onChange={(event) => setAdForm({ ...adForm, originalPrice: event.target.value })} />
                  <input placeholder="Offer price" type="number" value={adForm.offerPrice} onChange={(event) => setAdForm({ ...adForm, offerPrice: event.target.value })} />
                  <input placeholder="Duration days" type="number" value={adForm.durationDays} onChange={(event) => setAdForm({ ...adForm, durationDays: event.target.value })} />
                  <input type="datetime-local" value={adForm.scheduledAt} onChange={(event) => setAdForm({ ...adForm, scheduledAt: event.target.value })} />
                  <input type="date" value={adForm.validityDate} onChange={(event) => setAdForm({ ...adForm, validityDate: event.target.value })} />
                </div>
                <textarea placeholder="Description" value={adForm.description} onChange={(event) => setAdForm({ ...adForm, description: event.target.value })} />
                <div className="commerce-check-row">
                  <label><input type="checkbox" checked={adForm.isProductOffer} onChange={(event) => setAdForm({ ...adForm, isProductOffer: event.target.checked })} /> Product offer</label>
                  <label><input type="checkbox" checked={adForm.isFeatured} onChange={(event) => setAdForm({ ...adForm, isFeatured: event.target.checked })} /> Featured</label>
                  <label><input type="checkbox" checked={adForm.isPinned} onChange={(event) => setAdForm({ ...adForm, isPinned: event.target.checked })} /> Pinned</label>
                </div>
                <label className="commerce-upload">Poster image<input type="file" accept="image/*" onChange={(event) => uploadAdImage(event.target.files[0], 'posterImage')} /></label>
                <label className="commerce-upload">Product image<input type="file" accept="image/*" onChange={(event) => uploadAdImage(event.target.files[0], 'productImage')} /></label>
                <div className="commerce-preview-row">
                  {adForm.posterImage && <img src={adForm.posterImage} alt="" />}
                  {adForm.productImage && <img src={adForm.productImage} alt="" />}
                </div>
                <button type="submit">Create Ad</button>
              </form>
              <DataPanel
                title="Ads & Offers"
                filters={(
                  <div className="commerce-filter-row">
                    <select value={filters.adStatus} onChange={(event) => setFilters({ ...filters, adStatus: event.target.value })}>
                      <option value="">All status</option>
                      <option value="active">Active</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                    </select>
                    <button type="button" onClick={loadAds}>Filter</button>
                  </div>
                )}
              >
                <div className="commerce-table-wrap">
                  <table>
                    <thead><tr><th>Title</th><th>Shop</th><th>District</th><th>Price</th><th>Flags</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                      {ads.map((ad) => (
                        <tr key={ad._id}>
                          <td>{ad.title}</td>
                          <td>{ad.shopId?.name || 'N/A'}</td>
                          <td>{ad.district}</td>
                          <td>{ad.offerPrice ? money(ad.offerPrice) : 'N/A'}</td>
                          <td>{[ad.isProductOffer && 'Offer', ad.isFeatured && 'Featured', ad.isPinned && 'Pinned'].filter(Boolean).join(', ') || 'None'}</td>
                          <td><StatusBadge value={ad.status} /></td>
                          <td>
                            <div className="commerce-actions">
                              <button type="button" onClick={() => moderateAd(ad, { approvalStatus: 'approved', status: 'active' })}>Approve</button>
                              <button type="button" onClick={() => moderateAd(ad, { approvalStatus: 'rejected', rejectionReason: 'Rejected by Super Admin' })}>Reject</button>
                              <button type="button" onClick={() => moderateAd(ad, { isFeatured: !ad.isFeatured })}>{ad.isFeatured ? 'Unfeature' : 'Feature'}</button>
                              <button type="button" onClick={() => moderateAd(ad, { isPinned: !ad.isPinned })}>{ad.isPinned ? 'Unpin' : 'Pin'}</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination pageInfo={pagination.ads} onPage={(page) => setPage('ads', page)} />
              </DataPanel>
            </section>
          )}

          {activeTab === 'users' && (
            <DataPanel
              title="Users"
              filters={(
                <div className="commerce-filter-row">
                  <input placeholder="Email, name, phone" value={filters.userSearch} onChange={(event) => setFilters({ ...filters, userSearch: event.target.value })} />
                  <button type="button" onClick={loadUsers}>Filter</button>
                </div>
              )}
            >
              <div className="commerce-table-wrap">
                <table>
                  <thead><tr><th>User</th><th>Role</th><th>Shop</th><th>District</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>{user.email}<small>{user.name}</small></td>
                        <td>{user.role}</td>
                        <td>{user.shopId?.name || 'N/A'}</td>
                        <td>{user.district || 'N/A'}</td>
                        <td><StatusBadge value={user.status} /></td>
                        <td>
                          <div className="commerce-actions">
                            <button type="button" onClick={() => updateUser(user, { status: 'active' })}>Activate</button>
                            <button type="button" onClick={() => updateUser(user, { status: 'suspended', reason: 'Suspended by Super Admin' })}>Suspend</button>
                            <button type="button" onClick={() => updateUser(user, { status: 'blocked', reason: 'Blocked by Super Admin' })}>Block</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination pageInfo={pagination.users} onPage={(page) => setPage('users', page)} />
            </DataPanel>
          )}

          {activeTab === 'subscriptions' && (
            <section className="commerce-grid-with-form">
              <form className="commerce-panel commerce-form" onSubmit={submitSubscription}>
                <h2>Assign Plan</h2>
                <select value={subscriptionForm.shopId} onChange={(event) => setSubscriptionForm({ ...subscriptionForm, shopId: event.target.value })} required>
                  <option value="">Shop</option>
                  {shopOptions}
                </select>
                <select value={subscriptionForm.planId} onChange={(event) => setSubscriptionForm({ ...subscriptionForm, planId: event.target.value })} required>
                  <option value="">Plan</option>
                  {plans.map((plan) => <option key={plan._id} value={plan._id}>{plan.name} - {money(plan.price, plan.currency)}</option>)}
                </select>
                <input placeholder="Amount paid" type="number" value={subscriptionForm.amountPaid} onChange={(event) => setSubscriptionForm({ ...subscriptionForm, amountPaid: event.target.value })} />
                <select value={subscriptionForm.paymentStatus} onChange={(event) => setSubscriptionForm({ ...subscriptionForm, paymentStatus: event.target.value })}>
                  <option value="manual">Manual</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="waived">Waived</option>
                </select>
                <button type="submit">Assign Subscription</button>
              </form>
              <DataPanel title="Subscriptions">
                <div className="commerce-table-wrap">
                  <table>
                    <thead><tr><th>Shop</th><th>Plan</th><th>Status</th><th>Payment</th><th>Ends</th><th>Revenue</th></tr></thead>
                    <tbody>
                      {subscriptions.map((subscription) => (
                        <tr key={subscription._id}>
                          <td>{subscription.shopId?.name}</td>
                          <td>{subscription.planId?.name}</td>
                          <td><StatusBadge value={subscription.status} /></td>
                          <td>{subscription.paymentStatus}</td>
                          <td>{subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}</td>
                          <td>{money(subscription.amountPaid, subscription.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination pageInfo={pagination.subscriptions} onPage={(page) => setPage('subscriptions', page)} />
              </DataPanel>
            </section>
          )}

          {activeTab === 'nearby' && (
            <section className="commerce-panel">
              <div className="commerce-panel-header">
                <h2>Nearby API Test</h2>
                <div className="commerce-filter-row">
                  <input placeholder="Latitude" value={nearbyForm.lat} onChange={(event) => setNearbyForm({ ...nearbyForm, lat: event.target.value })} />
                  <input placeholder="Longitude" value={nearbyForm.lng} onChange={(event) => setNearbyForm({ ...nearbyForm, lng: event.target.value })} />
                  <input placeholder="Radius meters" type="number" value={nearbyForm.radius} onChange={(event) => setNearbyForm({ ...nearbyForm, radius: event.target.value })} />
                  <input placeholder="Category or product" value={nearbyForm.search} onChange={(event) => setNearbyForm({ ...nearbyForm, search: event.target.value })} />
                  <button type="button" onClick={testNearby}>Run</button>
                </div>
              </div>
              <div className="commerce-two-column">
                <div>
                  <h3>Offers</h3>
                  <div className="commerce-list">{nearbyOffers.map((offer) => <div key={offer._id} className="commerce-list-row"><span>{offer.title}</span><strong>{offer.distanceKm || 0} km</strong></div>)}</div>
                </div>
                <div>
                  <h3>Shops</h3>
                  <div className="commerce-list">{nearbyShops.map((shop) => <div key={shop._id} className="commerce-list-row"><span>{shop.name}</span><strong>{shop.distanceKm || 0} km</strong></div>)}</div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'districts' && (
            <DataPanel title="District Statistics">
              <div className="commerce-table-wrap">
                <table>
                  <thead><tr><th>District</th><th>Shops</th><th>Active Shops</th><th>Ads</th><th>Product Offers</th><th>Subscriptions</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {districtStats.map((district) => (
                      <tr key={district.district}>
                        <td>{district.district}</td>
                        <td>{district.totalShops || 0}</td>
                        <td>{district.activeShops || 0}</td>
                        <td>{district.totalAds || 0}</td>
                        <td>{district.productOffers || 0}</td>
                        <td>{district.activeSubscriptions || 0}</td>
                        <td>{money(district.revenue || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataPanel>
          )}

          {activeTab === 'media' && (
            <section className="commerce-panel">
              <div className="commerce-panel-header">
                <h2>Media Library</h2>
                <div className="commerce-filter-row">
                  <label className="commerce-upload">Upload ad<input type="file" accept="image/*" onChange={(event) => uploadMedia(event.target.files[0], 'ad')} /></label>
                  <label className="commerce-upload">Upload logo<input type="file" accept="image/*" onChange={(event) => uploadMedia(event.target.files[0], 'logo')} /></label>
                </div>
              </div>
              <div className="commerce-media-grid">
                {media.map((item) => (
                  <div key={`${item.folder}-${item.filename}`} className="commerce-media-item">
                    <img src={item.url} alt={item.filename} />
                    <span>{item.filename}</span>
                    <button type="button" onClick={() => navigator.clipboard?.writeText(item.url)}>Copy URL</button>
                    <button type="button" className="danger" onClick={() => deleteMedia(item)}>Delete</button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'notifications' && (
            <form className="commerce-panel commerce-form commerce-notification-form" onSubmit={sendNotification}>
              <h2>Send Notification</h2>
              <div className="commerce-form-grid">
                <select value={notificationForm.target} onChange={(event) => setNotificationForm({ ...notificationForm, target: event.target.value })}>
                  <option value="all">All</option>
                  <option value="shops">Shops</option>
                  <option value="users">Users</option>
                </select>
                <input placeholder="District" value={notificationForm.district} onChange={(event) => setNotificationForm({ ...notificationForm, district: event.target.value })} />
                <select value={notificationForm.type} onChange={(event) => setNotificationForm({ ...notificationForm, type: event.target.value })}>
                  <option value="system">System</option>
                  <option value="promotion">Promotion</option>
                  <option value="district">District</option>
                </select>
                <input placeholder="Title" value={notificationForm.title} onChange={(event) => setNotificationForm({ ...notificationForm, title: event.target.value })} required />
              </div>
              <textarea placeholder="Message" value={notificationForm.message} onChange={(event) => setNotificationForm({ ...notificationForm, message: event.target.value })} required />
              <button type="submit">Send Notification</button>
            </form>
          )}

          {activeTab === 'roles' && (
            <DataPanel title="Roles & Permissions">
              <div className="commerce-role-grid">
                {roles.map((role) => (
                  <div key={role.role} className="commerce-role">
                    <h3>{role.role}</h3>
                    <div>{role.permissions.map((permission) => <span key={permission}>{permission}</span>)}</div>
                  </div>
                ))}
              </div>
            </DataPanel>
          )}
        </main>
      </div>
    </div>
  );
};

const DataPanel = ({ title, filters, children }) => (
  <section className="commerce-panel">
    <div className="commerce-panel-header">
      <h2>{title}</h2>
      {filters}
    </div>
    {children}
  </section>
);

const Pagination = ({ pageInfo, onPage }) => {
  if (!pageInfo || pageInfo.pages <= 1) return null;
  return (
    <div className="commerce-pagination">
      <button type="button" disabled={pageInfo.page <= 1} onClick={() => onPage(pageInfo.page - 1)}>Previous</button>
      <span>{pageInfo.page} / {pageInfo.pages}</span>
      <button type="button" disabled={pageInfo.page >= pageInfo.pages} onClick={() => onPage(pageInfo.page + 1)}>Next</button>
    </div>
  );
};

export default CommerceAdmin;
